import React, {useEffect, useRef, useState} from 'react';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {withSystem, Plugins, Atom} from '@packages/systems/wfdl';
import {iconName, typeName, qualify} from '@packages/systems/wfdl-Qualified';
import {TTypeConstructor} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {getCdnUrl} from '@packages/systems/core/utils/UrlUtils';
import {
  useSite,
  usePreviewMode,
  useRuntimeEnvironment,
  useAsset,
} from '@packages/systems/render';
import {maybeRecoverAssetIdFromUrl} from '@packages/systems/assets';

import RiveElement from './RiveElement';

const placeholderStyle = {
  height: '100%',
  width: '100%',
} as const;

const useRiveUrl = (path: {src: string; id: string}) => {
  const runtimeEnvironment = useRuntimeEnvironment();
  const site = useSite();
  // Try to recover the asset using its id or recovered id from the src
  const asset = useAsset(path.id || maybeRecoverAssetIdFromUrl(path.src) || '');

  const isSsr = runtimeEnvironment === WebflowEnvironments.SSR;
  // We give preference to the asset's cdnUrl because it is the most up to date
  // information, since URLs change if the asset is replaced on the Assets Panel.
  // But historically Rive has relied on the src attribute - a hardcoded URL -
  // so we default to it to maintain backwards compatibility
  const url = asset ? asset.cdnUrl : path.src;

  if (isSsr) {
    return getCdnUrl(url, site.sslOk, site.cdnHost);
  } else {
    return url;
  }
};

const Rive = Atom.createRender(
  ({attributes, classes, generateAttributes, environmentContext, data}) => {
    const [container, setContainerRef] = useState(null);
    const moduleInstanceRef = useRef<{
      destroy: () => void;
    } | null>(null);

    const runtimeEnvironment = useRuntimeEnvironment();
    const isSsr = runtimeEnvironment === WebflowEnvironments.SSR;
    const isExport = runtimeEnvironment === WebflowEnvironments.EXPORT;
    const isPreviewMode = usePreviewMode();
    const isEditingIx2 = environmentContext.get('isEditingIx2ActionList');
    const {
      path,
      stateMachine,
      artboard,
      autoPlay,
      isTouchScrollEnabled,
      fit,
      alignment,
      automaticallyHandleEvents,
      // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
    } = data?.animation;

    const url = useRiveUrl(path);

    const props = {
      ...generateAttributes(attributes, classes),
      'data-rive-url': url,
      'data-rive-state-machine': stateMachine,
      'data-rive-artboard': artboard,
      'data-rive-autoplay': autoPlay,
      'data-rive-is-touch-scroll-enabled': isTouchScrollEnabled,
      'data-rive-automatically-handle-events': automaticallyHandleEvents,
      'data-rive-fit': fit,
      'data-rive-alignment': alignment,
      'data-animation-type': 'rive',
    } as const;

    const renderEmpty = isSsr || isExport || !container || !url;

    useEffect(() => {
      return () => {
        if (!moduleInstanceRef.current) return;
        // eslint-disable-next-line react-hooks/exhaustive-deps
        moduleInstanceRef.current.destroy();
      };
    }, []);

    return (
      // @ts-expect-error - TS2322 - Type 'Dispatch<SetStateAction<null>>' is not assignable to type 'LegacyRef<HTMLDivElement> | undefined'.
      <div {...props} ref={setContainerRef} data-animation-type="rive">
        {renderEmpty ? (
          <canvas style={placeholderStyle} />
        ) : (
          <RiveElement
            artboard={artboard}
            stateMachine={stateMachine}
            autoPlay={autoPlay}
            isTouchScrollEnabled={isTouchScrollEnabled}
            automaticallyHandleEvents={automaticallyHandleEvents}
            fit={fit}
            alignment={alignment}
            src={url}
            container={container}
            isEditingIx2={isEditingIx2}
            isPreviewMode={isPreviewMode}
            moduleInstanceRef={moduleInstanceRef}
            {...props}
          />
        )}
      </div>
    );
  }
);

const animationPlugin = Plugins.name('Animation');

const config = createIntegratedAtom({
  displayName: 'Rive',
  icon: {
    type: 'Component',
    value: qualify(animationPlugin, iconName('Rive')),
  },
  showEmpty: false,
  render: Rive,
  fields: {
    animation: withSystem(
      {
        label: 'Type',
        labelVariant: 'hidden',
      },
      TTypeConstructor(qualify(animationPlugin, typeName('Rive')))
    ),
  },
});

export default config;
